import { AggregationType, AggregationTypes } from '@carto/api-client'
import { ALL_FIELD_TYPES } from '@carto/kepler.gl/constants'

import { KeplerFieldType, MapDatasetEnriched, TimeSeriesWidget, TimeSeriesWidgetSerie } from 'features/builder/types'
import { isCustomAggregationExpSupported, DefaultOperationEntries, isValidOperation } from './utils'
import { MapType } from 'features/maps/types'

const supportedTypes = {
  column: [ALL_FIELD_TYPES.date, ALL_FIELD_TYPES.timestamp] as KeplerFieldType[],
  operationColumn: [ALL_FIELD_TYPES.integer, ALL_FIELD_TYPES.real] as KeplerFieldType[],
  splitByCategory: [ALL_FIELD_TYPES.string, ALL_FIELD_TYPES.boolean] as KeplerFieldType[]
}

export const timeSeriesSupportedFieldTypes = supportedTypes

export function isTimeSeriesWidgetValid(widget: TimeSeriesWidget, { source }: { source: MapDatasetEnriched }): boolean {
  const columnType = source.fields?.find((f) => f.id === widget.column)?.type as KeplerFieldType

  if (!columnType || !supportedTypes.column.includes(columnType)) {
    return false
  }
  if (widget.operation === AggregationTypes.Custom) {
    if (!widget.operationExp) {
      return false
    }
  } else if (widget.operation !== AggregationTypes.Count) {
    const operationColumnType = source.fields?.find((f) => f.id === widget.operationColumn)?.type as KeplerFieldType
    if (!supportedTypes.operationColumn.includes(operationColumnType)) {
      return false
    }
  }
  if (widget.splitByCategory) {
    const splitByCategoryType = source.fields?.find((f) => f.id === widget.splitByCategory)?.type as KeplerFieldType
    if (!splitByCategoryType || !supportedTypes.splitByCategory.includes(splitByCategoryType)) {
      return false
    }
  }

  if (widget.series) {
    const someSeriesInvalid = widget.series.some(({ operation, operationColumn }) => {
      if (operation === AggregationTypes.Count) {
        operationColumn = ''
      }
      return !isValidOperation({ operation, operationColumn, source, supportedTypes: supportedTypes.operationColumn })
    }, [] as TimeSeriesWidgetSerie[])
    if (someSeriesInvalid) {
      return false
    }
  }
  return true
}

export function validateTimeSeriesWidget(widget: TimeSeriesWidget, { source }: { source: MapDatasetEnriched }) {
  const availableOperations = timeSeriesWidgetAvailableOperations(source)
  const columnType = source.fields?.find((f) => f.id === widget.column)?.type as KeplerFieldType

  const operationColumnType = source.fields?.find((f) => f.id === widget.operationColumn)?.type as KeplerFieldType
  if (!widget.operation || (widget.operation && !availableOperations.includes(widget.operation))) {
    widget.operation = AggregationTypes.Count
  }
  if (!supportedTypes.column.includes(columnType)) {
    widget.column =
      source.fields?.find((f) => f.type === ALL_FIELD_TYPES.date || f.type === ALL_FIELD_TYPES.timestamp)?.id || ''
  }

  if (widget.operation === AggregationTypes.Count || !supportedTypes.operationColumn.includes(operationColumnType)) {
    widget.operationColumn = ''
  }

  widget.isValid = true
  if (!widget.column) {
    widget.isValid = false
  }

  if (widget.operation === AggregationTypes.Custom) {
    if (!widget.operationExp) {
      widget.isValid = false
    }
  } else if (widget.operation !== AggregationTypes.Count && !widget.operationColumn) {
    widget.isValid = false
  }

  if (widget.splitByCategory) {
    const fieldType = source.fields?.find((f) => f.id === widget.splitByCategory)?.type as KeplerFieldType
    if (!fieldType || !supportedTypes.splitByCategory.includes(fieldType)) {
      widget.splitByCategory = undefined
    } else {
      widget.splitByCategoryLimit = 10
    }
  }

  if (widget.series) {
    for (const serie of widget.series) {
      if (!isValidOperation({ ...serie, source })) {
        widget.isValid = false
      }
    }

    if (widget.series.length === 0) {
      widget.series = undefined
    }
  }
}

export function timeSeriesWidgetAvailableOperations(source: MapDatasetEnriched): AggregationType[] {
  const supportsCustomAggregation = isCustomAggregationExpSupported({ source })
  const supportsNormalAggregation = source.fields.some((f) => supportedTypes.operationColumn.includes(f.type))

  const result: AggregationType[] = []
  if (!supportsNormalAggregation) {
    result.push(AggregationTypes.Count)
  } else {
    result.push(...DefaultOperationEntries)
  }
  if (supportsCustomAggregation) {
    result.push(AggregationTypes.Custom)
  }
  return result
}

export function isWidgetTimeSeriesAvailable({
  source,
  sourceHasTimeSeriesWidget
}: {
  source: MapDatasetEnriched
  sourceHasTimeSeriesWidget: boolean
}) {
  const noOtherTsWidget = !sourceHasTimeSeriesWidget
  const isRaster = source.metadata?.sourceType === MapType.raster
  const hasTimeField = source.fields && source.fields.some((f) => supportedTypes.column.includes(f.type))

  const unavailableTooltipId = isRaster
    ? 'builder.widgets.notAvailableTooltipRaster'
    : !hasTimeField
    ? 'builder.widgets.timeSeries.notAvailableTooltipNoTimeColumn'
    : !noOtherTsWidget
    ? 'builder.widgets.timeSeries.notAvailableTooltipOther'
    : undefined
  const isAvailable = !isRaster && noOtherTsWidget && hasTimeField

  return { isAvailable, unavailableTooltipId }
}
